import { CommonModule, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { AppIcon } from 'core/components/icons/icon.type';
import { IconsModule } from 'core/components/icons/icons.module';
import { AppIconsLibrary } from 'core/components/icons/icons-library.service';
import { WindowService } from 'core/services/window.service';

@Component({
  selector: 'app-ui-check-box',
  standalone: true,
  imports: [ReactiveFormsModule, IconsModule, NgClass],
  templateUrl: './ui-check-box.component.html',
  styleUrl: './styles/checkbox.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef((): typeof UiCheckBoxComponent => UiCheckBoxComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiCheckBoxComponent {
  readonly iconCheckEmpty: AppIcon | undefined;
  readonly iconCheckActive: AppIcon | undefined;
  readonly iconCheckRoundEmpty: AppIcon | undefined;
  readonly iconCheckRoundActive: AppIcon | undefined;
  readonly iconCheckFrameActive: AppIcon | undefined;

  @Input()
  id: string = WindowService.uuid();
  @Input()
  label: string = '';
  @Input()
  type: 'square' | 'round' | 'square-frame' = 'square';
  @HostBinding('class.disabled')
  @Input()
  disabled?: boolean;

  @Output()
  changed$ = new EventEmitter<boolean>();

  protected control = new FormControl(false);

  private onTouchedCallback: (_: boolean | null) => void = (): void => {};
  private onChangeCallback: (_: boolean | null) => void = (): void => {};

  get value() {
    return this.control.value;
  }

  @Input()
  set value(v: boolean | null) {
    if (v !== this.control.value) {
      this.writeValue(v);
      this.onChangeCallback(v);
    }
  }

  constructor(
    icons: AppIconsLibrary,
    private renderer: Renderer2,
    private elRef: ElementRef<HTMLElement>,
  ) {
    this.iconCheckEmpty = icons.getIcon('check-empty');
    this.iconCheckActive = icons.getIcon('check-active');
    this.iconCheckRoundEmpty = icons.getIcon('check-round-empty');
    this.iconCheckRoundActive = icons.getIcon('check-round-active');
    this.iconCheckFrameActive = icons.getIcon('check-frame-active');

    this.control.valueChanges.pipe(takeUntilDestroyed()).subscribe(v => {
      this.onTouchedCallback(v);
      this.onChangeCallback(v);
      this.changed$.next(v || false);
    });
  }

  writeValue(value: boolean | null): void {
    if (value !== this.control.value) {
      this.control.setValue(value);
    }
  }

  registerOnChange(fn: (val: boolean | null) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: (val: boolean | null) => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.renderer.setAttribute(this.elRef.nativeElement, 'disabled', '');
    } else {
      this.renderer.removeAttribute(this.elRef.nativeElement, 'disabled');
    }
  }
}
